import * as React from "react";
import { useMutation } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  usePointsBalanceForUser,
  usePointsHistoryForUser,
  useOrdersForPointsAdjustment,
  useOverduePaymentsForUser,
} from "@/hooks/useAccounting";
import { formatCurrency, formatDate, formatStatusLabel } from "@/lib/format";
import {
  formatPointsLedgerTypeLabel,
  getPointsLedgerTypeBadgeClass,
} from "@/lib/points-ledger";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const ADJUSTMENT_REASONS = [
  "customer_goodwill",
  "order_issue_resolution",
  "manual_correction",
  "promotional_credit",
  "late_payment_forfeiture",
  "account_migration",
  "other",
] as const;

const REASON_LABELS: Record<string, string> = {
  customer_goodwill: "Customer Goodwill",
  order_issue_resolution: "Order Issue Resolution",
  manual_correction: "Manual Correction",
  promotional_credit: "Promotional Credit",
  late_payment_forfeiture: "Late Payment Forfeiture",
  account_migration: "Account Migration",
  other: "Other",
};

export function PointsTab({ userId }: { userId: string }) {
  const balance = usePointsBalanceForUser(userId);
  const history = usePointsHistoryForUser(userId);
  const overdueSummary = useOverduePaymentsForUser(userId);
  const [adjustOpen, setAdjustOpen] = React.useState(false);

  return (
    <div className="space-y-6">
      {balance === undefined ? (
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardContent className="space-y-3 p-6">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-8 w-32" />
              <Skeleton className="h-4 w-28" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="space-y-3 p-6">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-4 w-36" />
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardContent className="flex items-center justify-between p-6">
              <div>
                <p className="text-xs text-muted-foreground">Points Balance</p>
                <p className="text-3xl font-bold">
                  {(balance.points ?? 0).toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">
                  ≈ {formatCurrency(balance.dollarValue ?? 0)}
                </p>
              </div>
              <Button onClick={() => setAdjustOpen(true)}>
                Adjust Points
              </Button>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <p className="text-xs text-muted-foreground">Overdue Payments</p>
              <p className="text-3xl font-bold">
                {(overdueSummary?.overduePayments?.length ?? 0).toLocaleString()}
              </p>
              <p className="text-sm text-muted-foreground">
                Total overdue: {formatCurrency(overdueSummary?.totalOverdue ?? 0)}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {history === undefined ? (
        <Card>
          <CardHeader>
            <CardTitle>
              Points History
              <Skeleton className="ml-2 inline-block h-4 w-16 align-middle" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, idx) => (
                <Skeleton key={idx} className="h-10 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      ) : history.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Points History</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="py-8 text-center text-sm text-muted-foreground">
              No points activity yet. Adjustments and earned points will appear here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>
              Points History
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({history.length} {history.length === 1 ? "entry" : "entries"})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="text-muted-foreground hover:bg-transparent">
                  <TableHead className="font-medium">Date</TableHead>
                  <TableHead className="font-medium">Type</TableHead>
                  <TableHead className="font-medium text-right">Amount</TableHead>
                  <TableHead className="font-medium text-right">
                    Balance After
                  </TableHead>
                  <TableHead className="font-medium">Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((entry: any) => {
                  const amount = Number(entry.amount ?? 0);
                  const amountText = `${amount > 0 ? "+" : ""}${amount.toLocaleString()}`;
                  return (
                    <TableRow key={entry.id}>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatDate(entry.createdAt)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={cn(
                            getPointsLedgerTypeBadgeClass(entry.type),
                            "text-xs",
                          )}
                        >
                          {formatPointsLedgerTypeLabel(entry.type)}
                        </Badge>
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-right font-medium tabular-nums",
                          amount > 0 ? "text-emerald-600" : "text-red-600",
                        )}
                      >
                        {amountText}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {Number(entry.balanceAfter ?? 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="max-w-[420px] text-xs text-muted-foreground">
                        <span className="line-clamp-2">
                          {entry.description}
                          {entry.orderNumber
                            ? ` (Order #${entry.orderNumber})`
                            : ""}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {adjustOpen ? (
        <AdjustPointsDialog
          userId={userId}
          currentBalance={balance?.points ?? 0}
          onClose={() => setAdjustOpen(false)}
        />
      ) : null}
    </div>
  );
}

function AdjustPointsDialog({
  userId,
  currentBalance,
  onClose,
}: {
  userId: string;
  currentBalance: number;
  onClose: () => void;
}) {
  const adjustMutation = useMutation(api.pointsLedger.adminAdjustPoints);
  const orders = useOrdersForPointsAdjustment(userId);

  const [operation, setOperation] = React.useState<"add" | "subtract">("add");
  const [amount, setAmount] = React.useState(0);
  const [reason, setReason] = React.useState("");
  const [orderId, setOrderId] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);

  const newBalance =
    operation === "add"
      ? currentBalance + amount
      : currentBalance - amount;

  const handleSave = React.useCallback(async () => {
    if (!reason) {
      toast.error("Reason is required");
      return;
    }
    if (amount <= 0) {
      toast.error("Amount must be positive");
      return;
    }
    setIsSaving(true);
    try {
      const adjustedAmount = operation === "add" ? amount : -amount;
      const selectedOrder = orders?.find((o: any) => o.id === orderId);
      await adjustMutation({
        userId: userId as any,
        amount: adjustedAmount,
        reason,
        notes: notes.trim() || undefined,
        orderId: orderId ? (orderId as any) : undefined,
        orderNumber: selectedOrder?.orderNumber,
      });
      toast.success("Points adjusted");
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to adjust points");
    } finally {
      setIsSaving(false);
    }
  }, [userId, operation, amount, reason, orderId, notes, orders, adjustMutation, onClose]);

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Adjust Points</DialogTitle>
          <DialogDescription>
            Current balance: {currentBalance.toLocaleString()} pts
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded border p-3 text-center">
              <p className="text-xs text-muted-foreground">Current</p>
              <p className="text-lg font-bold">{currentBalance.toLocaleString()}</p>
            </div>
            <div className="rounded border p-3 text-center">
              <p className="text-xs text-muted-foreground">After</p>
              <p className={`text-lg font-bold ${newBalance < 0 ? "text-red-600" : ""}`}>
                {newBalance.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="grid gap-2">
            <Label>Operation</Label>
            <div className="flex gap-2">
              <Button
                variant={operation === "add" ? "default" : "outline"}
                size="sm"
                onClick={() => setOperation("add")}
              >
                Add Points
              </Button>
              <Button
                variant={operation === "subtract" ? "default" : "outline"}
                size="sm"
                onClick={() => setOperation("subtract")}
              >
                Subtract Points
              </Button>
            </div>
          </div>

          <div className="grid gap-2">
            <Label>Amount</Label>
            <Input
              type="number"
              min={1}
              value={amount || ""}
              onChange={(e) => setAmount(parseInt(e.target.value) || 0)}
            />
          </div>

          <div className="grid gap-2">
            <Label>Reason category (required)</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger>
                <SelectValue placeholder="Select reason..." />
              </SelectTrigger>
              <SelectContent>
                {ADJUSTMENT_REASONS.map((r) => (
                  <SelectItem key={r} value={r}>
                    {REASON_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label>Related Order (optional)</Label>
            <Select value={orderId} onValueChange={setOrderId}>
              <SelectTrigger>
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {orders?.map((o: any) => (
                  <SelectItem key={o.id} value={o.id}>
                    #{o.orderNumber} · {formatCurrency(o.total)} · {formatStatusLabel(o.status)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label>Description shown to customer (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Appears in the customer's rewards history..."
              maxLength={500}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={isSaving || amount <= 0 || !reason}
          >
            {isSaving ? "Saving..." : "Adjust Points"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
